/**
 * Move a key to the end of the list if present (display-only pin).
 * Does not mutate saved column preferences.
 */
export const pinKeyLast = (keys = [], key = "action") => {
  if (!Array.isArray(keys) || !keys.includes(key)) {
    return keys;
  }
  return [...keys.filter((item) => item !== key), key];
};

/**
 * Reorder table column definitions to match a saved key order.
 * Keys not present in orderKeys are appended in their original order.
 */
export const orderColumnsByKeys = (columns, orderKeys = []) => {
  if (!orderKeys.length) {
    return columns;
  }

  const normalizedOrderKeys = [...new Set(orderKeys)];
  const columnMap = new Map(columns.map((column) => [column.key, column]));
  const ordered = normalizedOrderKeys
    .map((key) => columnMap.get(key))
    .filter(Boolean);
  const orderedKeySet = new Set(normalizedOrderKeys);
  const remaining = columns.filter((column) => !orderedKeySet.has(column.key));

  return [...ordered, ...remaining];
};

/**
 * Clean a saved visible-column selection while KEEPING the user's saved order.
 * The saved array is authoritative for display order, so column reordering
 * persists across save + refresh (NCK-469 — the backend stores rfi_columns /
 * submittal_columns as an ordered list and returns it verbatim).
 *
 * This only removes duplicates and pushes keys that aren't selectable in the
 * current context (workflow- or terminology-specific columns) to the end; it no
 * longer re-anchors the order to the hardcoded master order.
 */
export const normalizeColumnSelectionOrder = (
  selectedKeys = [],
  masterOrder = [],
) => {
  if (!Array.isArray(selectedKeys) || selectedKeys.length === 0) {
    return [];
  }

  const masterSet = new Set(masterOrder);
  const seen = new Set();
  const selectableKeys = [];
  const otherKeys = [];

  selectedKeys.forEach((key) => {
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    if (masterSet.size === 0 || masterSet.has(key)) {
      selectableKeys.push(key);
    } else {
      otherKeys.push(key);
    }
  });

  return [...selectableKeys, ...otherKeys];
};

/**
 * Build a full selectable column list that keeps hidden columns in their
 * default positions while preserving the user's visible-column order.
 */
export const buildFullColumnOrder = (visibleOrder = [], masterOrder = []) => {
  const visibleSet = new Set(visibleOrder);
  const visibleQueue = visibleOrder.filter((key) => masterOrder.includes(key));

  return masterOrder.map((key) => {
    if (visibleSet.has(key)) {
      return visibleQueue.shift();
    }
    return key;
  });
};

export const moveItemInArray = (items, index, direction) => {
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= items.length) {
    return items;
  }

  const nextItems = [...items];
  [nextItems[index], nextItems[targetIndex]] = [
    nextItems[targetIndex],
    nextItems[index],
  ];
  return nextItems;
};

/**
 * Visible columns in display order (excludes keys not in orderedKeys).
 */
export const getVisibleColumnsFromOrder = (orderedKeys, selectedKeys) =>
  orderedKeys.filter((key) => selectedKeys.includes(key));
